import { useEffect, useState } from "react";
import type { Criterion, HiringMatrix, Position } from "../types";
import { api } from "../lib/api";

interface Props {
  positions: Position[];
  onPositionsChange: () => void;
}

const emptyCriterion: Criterion = { name: "", weight: 10, requirement: "" };
const defaultCriteria: Criterion[] = [
  { name: "Years of experience", weight: 25, requirement: "3+ years" },
  { name: "Required skills", weight: 40, requirement: "List required skills" },
  { name: "Education", weight: 15, requirement: "BS or equivalent" },
  { name: "Communication", weight: 20, requirement: "Clear written + verbal" },
];

export function MatrixManager({ positions, onPositionsChange }: Props) {
  const [matrices, setMatrices] = useState<HiringMatrix[]>([]);
  const [loading, setLoading] = useState(true);

  // Search + filters
  const [search, setSearch] = useState("");
  const [filterPositionId, setFilterPositionId] = useState<string>("all");
  const [filtersOpen, setFiltersOpen] = useState(false);

  // New matrix form
  const [open, setOpen] = useState(false);
  const [positionId, setPositionId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [criteria, setCriteria] = useState<Criterion[]>(defaultCriteria);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New position inline form
  const [showNewPosition, setShowNewPosition] = useState(false);
  const [newPosTitle, setNewPosTitle] = useState("");
  const [newPosDesc, setNewPosDesc] = useState("");

  const totalWeight = criteria.reduce((s, c) => s + (Number(c.weight) || 0), 0);

  const refresh = async () => {
    try {
      const { matrices } = await api.listMatrices();
      setMatrices(matrices);
    } catch {
      /* ignore */
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  // Default position selection
  useEffect(() => {
    if (positionId == null && positions.length > 0) {
      setPositionId(positions[0].id);
    }
  }, [positions, positionId]);

  const createPosition = async () => {
    if (!newPosTitle.trim()) return;
    try {
      const { position } = await api.createPosition({
        title: newPosTitle.trim(),
        description: newPosDesc.trim() || undefined,
      });
      setNewPosTitle("");
      setNewPosDesc("");
      setShowNewPosition(false);
      setPositionId(position.id);
      onPositionsChange();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const submit = async () => {
    setError(null);
    if (!positionId) return setError("Select a position first");
    if (!name.trim()) return setError("Matrix name is required");
    if (criteria.some((c) => !c.name.trim() || !c.requirement.trim())) {
      return setError("Each criterion needs a name and a requirement");
    }
    if (totalWeight <= 0) return setError("Weights must sum to > 0");

    setSaving(true);
    try {
      await api.createMatrix({
        position_id: positionId,
        name: name.trim(),
        criteria,
      });
      setName("");
      setCriteria([...defaultCriteria]);
      setOpen(false);
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!confirm("Delete this hiring matrix and all associated candidates?")) return;
    await api.deleteMatrix(id);
    refresh();
  };

  const removePosition = async (id: number) => {
    if (!confirm("Delete this position, all its matrices, and all its candidates?"))
      return;
    await api.deletePosition(id);
    onPositionsChange();
    refresh();
  };

  // Filter matrices
  const filteredMatrices = matrices.filter((m) => {
    const q = search.toLowerCase().trim();
    if (q) {
      const nameMatch = m.name.toLowerCase().includes(q);
      const criteriaMatch = m.criteria.some(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.requirement.toLowerCase().includes(q)
      );
      const posTitle = (m.position_title ?? "").toLowerCase().includes(q);
      if (!nameMatch && !criteriaMatch && !posTitle) return false;
    }
    if (filterPositionId !== "all" && m.position_id !== Number(filterPositionId)) {
      return false;
    }
    return true;
  });

  // Group filtered matrices by position
  const grouped = positions
    .map((p) => ({
      position: p,
      matrices: filteredMatrices.filter((m) => m.position_id === p.id),
    }))
    .filter(
      (g) =>
        g.matrices.length > 0 ||
        (filterPositionId === "all" && search === "")
    );

  const activeFilterCount = filterPositionId !== "all" ? 1 : 0;
  const hasActiveFilters = search !== "" || activeFilterCount > 0;

  const clearFilters = () => {
    setSearch("");
    setFilterPositionId("all");
  };

  // Counts per position
  const positionCounts: Record<number, number> = {};
  for (const m of matrices) {
    positionCounts[m.position_id] = (positionCounts[m.position_id] || 0) + 1;
  }

  return (
    <div className="space-y-6">
      {/* Header + search + filter toggle + add button */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <h2 className="text-[28px] font-bold leading-[1.43] text-near-black">
            Hiring Matrices
          </h2>
          <span className="rounded-badge bg-near-black px-2.5 py-0.5 text-xs font-semibold tabular-nums text-white">
            {matrices.length}
          </span>
        </div>
        <div className="flex flex-1 items-center justify-end gap-2 sm:max-w-lg">
          <div className="relative flex-1 sm:max-w-xs">
            <svg
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
              />
            </svg>
            <input
              className="w-full rounded-btn border border-border-card bg-white py-2 pl-10 pr-3 text-sm text-near-black placeholder:text-text-secondary focus:outline-none focus:shadow-focus"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={[
              "flex shrink-0 items-center gap-1.5 rounded-btn px-4 py-2 text-sm font-medium transition-colors",
              filtersOpen || activeFilterCount > 0
                ? "bg-near-black text-white"
                : "bg-surface-secondary text-text-focused hover:bg-near-black hover:text-white",
            ].join(" ")}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
            </svg>
            Filters
            {activeFilterCount > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-rausch text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            className="shrink-0 rounded-btn bg-near-black px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch"
            onClick={() => setOpen(!open)}
          >
            {open ? "Cancel" : "+ Add"}
          </button>
        </div>
      </div>

      {/* Collapsible filter panel */}
      {filtersOpen && (
        <div className="rounded-card bg-white p-5 shadow-card">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
                Position
              </label>
              <select
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
                value={filterPositionId}
                onChange={(e) => setFilterPositionId(e.target.value)}
              >
                <option value="all">All positions</option>
                {positions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title} {positionCounts[p.id] ? `(${positionCounts[p.id]})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div />
            <div />
            <div />
          </div>
          {hasActiveFilters && (
            <div className="mt-3 flex items-center justify-between">
              <p className="text-xs text-text-secondary">
                Showing {filteredMatrices.length} of {matrices.length} matrices
              </p>
              <button
                onClick={clearFilters}
                className="text-xs font-medium text-rausch hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* New matrix form */}
      {open && (
        <div className="rounded-card bg-white p-5 shadow-card">
          <div className="space-y-4">
            {/* Position selector */}
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-text-secondary">
                  Position
                </label>
                <button
                  className="text-xs font-medium text-rausch hover:underline"
                  onClick={() => setShowNewPosition(!showNewPosition)}
                >
                  {showNewPosition ? "Cancel" : "+ New position"}
                </button>
              </div>
              {showNewPosition ? (
                <div className="mt-2 space-y-2 rounded-badge bg-surface-secondary p-3">
                  <input
                    className="w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
                    placeholder="Position title"
                    value={newPosTitle}
                    onChange={(e) => setNewPosTitle(e.target.value)}
                  />
                  <input
                    className="w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
                    placeholder="Description (optional)"
                    value={newPosDesc}
                    onChange={(e) => setNewPosDesc(e.target.value)}
                  />
                  <button
                    className="rounded-btn bg-near-black px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-rausch"
                    onClick={createPosition}
                  >
                    Create position
                  </button>
                </div>
              ) : (
                <select
                  className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
                  value={positionId ?? ""}
                  onChange={(e) => setPositionId(Number(e.target.value) || null)}
                >
                  {positions.length === 0 && (
                    <option value="">— create a position first —</option>
                  )}
                  {positions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Matrix name */}
            <div>
              <label className="text-xs font-semibold text-text-secondary">
                Matrix name
              </label>
              <input
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Standard Technical Screen"
              />
            </div>

            {/* Criteria */}
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-text-secondary">
                  Criteria
                </label>
                <span className="text-xs font-medium text-text-secondary">
                  Total weight: {totalWeight}
                </span>
              </div>
              <div className="mt-2 space-y-2">
                {criteria.map((c, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-12 gap-2 rounded-btn border border-border-card bg-surface-secondary p-2"
                  >
                    <input
                      className="col-span-3 rounded-btn border border-border-card bg-white px-2 py-1 text-sm text-near-black focus:outline-none focus:shadow-focus"
                      placeholder="Criterion"
                      value={c.name}
                      onChange={(e) =>
                        setCriteria(
                          criteria.map((x, j) =>
                            j === i ? { ...x, name: e.target.value } : x
                          )
                        )
                      }
                    />
                    <input
                      type="number"
                      min={0}
                      className="col-span-2 rounded-btn border border-border-card bg-white px-2 py-1 text-sm text-near-black focus:outline-none focus:shadow-focus"
                      placeholder="Weight"
                      value={c.weight}
                      onChange={(e) =>
                        setCriteria(
                          criteria.map((x, j) =>
                            j === i
                              ? { ...x, weight: Number(e.target.value) }
                              : x
                          )
                        )
                      }
                    />
                    <input
                      className="col-span-6 rounded-btn border border-border-card bg-white px-2 py-1 text-sm text-near-black focus:outline-none focus:shadow-focus"
                      placeholder="Requirement"
                      value={c.requirement}
                      onChange={(e) =>
                        setCriteria(
                          criteria.map((x, j) =>
                            j === i
                              ? { ...x, requirement: e.target.value }
                              : x
                          )
                        )
                      }
                    />
                    <button
                      className="col-span-1 rounded-full text-sm text-text-secondary transition-colors hover:bg-rose-100 hover:text-error-text"
                      onClick={() =>
                        setCriteria(criteria.filter((_, j) => j !== i))
                      }
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <button
                className="mt-2 text-sm font-medium text-rausch hover:underline"
                onClick={() => setCriteria([...criteria, { ...emptyCriterion }])}
              >
                + Add criterion
              </button>
            </div>

            {error && <p className="text-sm font-medium text-error-text">{error}</p>}

            <div className="flex justify-end">
              <button
                disabled={saving}
                className="rounded-btn bg-near-black px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch disabled:bg-border-card disabled:text-text-secondary"
                onClick={submit}
              >
                {saving ? "Saving…" : "Save matrix"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && <p className="text-text-secondary">Loading…</p>}

      {/* Grouped list */}
      {!loading && grouped.length === 0 && (
        <div className="rounded-card border-2 border-dashed border-border-card bg-white p-10 text-center text-text-secondary">
          {matrices.length === 0
            ? "No matrices yet. Create one to start screening."
            : "No matrices match the current filters."}
        </div>
      )}

      {!loading &&
        grouped.map(({ position, matrices: posMatrices }) => (
          <div key={position.id} className="rounded-card bg-white p-5 shadow-card">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-[20px] font-semibold leading-tight tracking-[-0.18px] text-near-black">
                  {position.title}
                </h3>
                {position.description && (
                  <p className="mt-0.5 text-sm text-text-secondary">
                    {position.description}
                  </p>
                )}
              </div>
              <button
                onClick={() => removePosition(position.id)}
                className="text-xs text-text-secondary transition-colors hover:text-error-text"
              >
                Delete position
              </button>
            </div>

            {posMatrices.length === 0 ? (
              <p className="mt-3 text-sm text-text-secondary">
                No matrices yet for this position.
              </p>
            ) : (
              <div className="mt-4 space-y-3">
                {posMatrices.map((m) => (
                  <div
                    key={m.id}
                    className="rounded-badge border border-surface-secondary bg-surface-secondary p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-semibold text-near-black">
                          {m.name}
                        </div>
                        <div className="mt-1 text-xs text-text-secondary">
                          {m.criteria.length} criteria ·{" "}
                          {m.criteria.map((c) => c.name).join(", ")}
                        </div>
                      </div>
                      <button
                        onClick={() => remove(m.id)}
                        className="text-xs text-text-secondary transition-colors hover:text-error-text"
                      >
                        Delete
                      </button>
                    </div>
                    <div className="mt-2 space-y-1">
                      {m.criteria.map((c) => (
                        <div key={c.name} className="flex items-center gap-2 text-xs">
                          <span className="font-medium text-text-focused">
                            {c.name}
                          </span>
                          <span className="text-text-secondary">
                            w{c.weight}
                          </span>
                          <span className="text-text-secondary">
                            — {c.requirement}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
    </div>
  );
}
